import { useMutation, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/components/Auth";
import {
  bookAppointment,
  fetchAvailableTimes,
  fetchDashboard,
  fetchFeatures,
  fetchServiceRecommendations,
  fetchServices,
  rescheduleAppointment,
} from "@/services/booking";
import { isIVNutrientsServiceDynamic } from "@/utils/ivNutrients";

export const DashboardQueryKey = ["dashboard"];

export const useDashboardQuery = () =>
  useQuery({
    queryKey: DashboardQueryKey,
    queryFn: fetchDashboard,
  });

export const useFeaturesQuery = () => {
  const { isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["features"],
    queryFn: fetchFeatures,
    enabled: isSignedIn,
  });
};

export const useServiceRecommendationsQuery = () =>
  useQuery({
    queryKey: ["service-recommendations"],
    queryFn: fetchServiceRecommendations,
  });

type UseAvailableTimesQueryParams = {
  storeCode: string;
  serviceId?: string | number;
  date?: string;
};

export const useAvailableTimesQuery = ({
  storeCode,
  serviceId,
  date,
}: UseAvailableTimesQueryParams) =>
  useQuery({
    queryKey: ["available-times", storeCode, serviceId, date],
    enabled: Boolean(serviceId && date),
    queryFn: () => {
      if (!serviceId || !date) {
        return Promise.reject(new Error("Invalid parameters"));
      }
      return fetchAvailableTimes(storeCode, serviceId, date);
    },
  });

export const useServicesQuery = (storeCode: string) =>
  useQuery({
    queryKey: ["services", storeCode],
    queryFn: () => fetchServices(storeCode),
  });

/**
 * Hook to check if a service is an IV nutrients service using dynamic API data
 */
export const useIsIVNutrientsService = (
  storeCode: string,
  serviceId: number | string,
) => {
  const servicesQuery = useServicesQuery(storeCode);

  return {
    isIVNutrientsService: servicesQuery.data
      ? isIVNutrientsServiceDynamic(serviceId, servicesQuery.data)
      : false,
    isLoading: servicesQuery.isLoading,
    error: servicesQuery.error,
  };
};

export const useBookAppointmentMutation = () =>
  useMutation({ mutationFn: bookAppointment });

export const useRescheduleMutation = () =>
  useMutation({ mutationFn: rescheduleAppointment });
